import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { standardEventTypes } from '../utils';
import { resolveEventTypes } from '../pages/services/serviceConfigurationRules';
import { useViewedTenantAccount } from './useViewedTenantAccount';
import { spaceOf } from '../utils/useCases';

/** The screen's own use case when the caller names none. */
const DEFAULT_EVENT_USE_CASE = 'venue';

/** The use case the vocabulary is stored on: the caller's event-flow use case
 *  (venue, hall), else venue, the screen's own. */
const eventFlowUseCaseOf = (useCase?: string | null): string => {
    const value = String(useCase ?? '').trim().toLowerCase();
    return spaceOf(value)?.eventTypes === true ? value : DEFAULT_EVENT_USE_CASE;
};

/**
 * The event types a venue dropdown should offer — the tenant's own vocabulary
 * where it configured one, the standard localized list otherwise.
 *
 * THE SINGLE READER of `payments.useCases.<useCase>.eventTypes` in the admin-ui
 * (`docs/plans/tenant-configurable-event-types.md`). Four screens ask this
 * question — create, series, edit and the booking detail — and answering it
 * inline in each is how the two hardcoded copies drifted in the first place
 * (#2488 → #2492).
 *
 * Absence is the documented default, never a guess: a tenant that configured
 * nothing, and every LEGACY tenant (which has no operational account record at
 * all, so the account queries do not even fire), gets exactly today's list in
 * today's language.
 *
 * The tenant is resolved through `useViewedTenantAccount` rather than a bare
 * `useGetAccountQuery`, so a master creating a booking for another tenant sees
 * THAT tenant's vocabulary and not their own (#1334, #2059). The Stripe status
 * that hook can also resolve is opted out of — a booking screen never shows it,
 * and asking would cost a live Stripe round-trip per visit.
 */
export const useEventTypeOptions = (
    selected?: string | null,
    /** The event-flow use case whose vocabulary is offered: venue, or hall,
     *  which shares the venue screens. Absent = venue, as before. */
    useCase?: string | null,
): string[] => {
    const { i18n } = useTranslation();
    const { accountTenant } = useViewedTenantAccount({ withPaymentIntegration: false });

    const configured = accountTenant?.payments?.useCases?.[eventFlowUseCaseOf(useCase)]?.eventTypes;
    const language = i18n.language;

    return useMemo(() => {
        const options = resolveEventTypes(configured, standardEventTypes(language));

        /*
         * A booking keeps the label it was created with (plan decision 4), so a
         * type the tenant has since renamed or removed still sits on older
         * bookings. Without this the edit and detail dropdowns would render
         * BLANK for those — a stored value silently missing from the screen
         * showing it, which is the failure-as-absence shape exactly.
         *
         * Appended rather than inserted: it is history, not part of the
         * vocabulary on offer.
         */
        const trimmed = selected?.trim();
        if (trimmed && !options.includes(trimmed)) return [...options, trimmed];
        return options;
    }, [configured, language, selected]);
};

export default useEventTypeOptions;
